/* eslint-disable react-hooks/exhaustive-deps */
"use client";

import { Icons } from "@/components/icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberField, QuantityField } from "@/components/ui/number-field";
import { usePixelTracking } from "@/hooks/usePixelTracking";
import { axiosInstance } from "@/lib/axios";
import { queryClient } from "@/lib/query-client";
import { EventType, TicketType } from "@/lib/types";
import { cn, formatRupiah, formatYen, formatDollar } from "@/lib/utils";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { useNumberField } from "react-aria";
import { useForm } from "react-hook-form";
import { NumberFieldStateOptions, useNumberFieldState } from "react-stately";

export type TicketOptionType = {
  id: string;
  type: "free" | "fixed_price" | "minimum_price";
  price: number;
  quantity: number;
};

type FormData = {
  tickets: Array<TicketOptionType>;
};

function buildTicketSelectionMetadata({
  event,
  selectedTickets,
}: {
  event: EventType;
  selectedTickets: TicketOptionType[];
}) {
  const selected = selectedTickets
    .filter((ticket) => ticket.quantity > 0)
    .map((ticket) => {
      return {
        id: ticket.id,
        quantity: ticket.quantity,
        item_price: ticket.price,
      };
    });

  return {
    content_ids: selected.map((ticket) => ticket.id),
    content_name: event.title,
    content_type: "product",
    contents: selected.map(({ id, quantity, item_price }) => ({
      id,
      quantity,
      item_price,
    })),
    currency: event.currency_type,
    value: selected.reduce(
      (total, ticket) => total + ticket.quantity * ticket.item_price,
      0,
    ),
  };
}

export function TicketSelector({
  event,
}: {
  event: EventType & {
    need_refresh: boolean;
  };
}) {
  const router = useRouter();
  const pageSlug = event.current_slug ?? event.slug;

  useEffect(() => {
    // Make sure to reset RSC in order to
    // track page view correctly, since nextjs
    // will not set the session id after first visit
    // And only client side navigation will get
    // the new session id
    let id = setTimeout(() => {
      if (event.need_refresh) {
        router.refresh();
      }
    }, 1000);

    return () => {
      clearTimeout(id);
    };
  }, [event.need_refresh]);

  const params = useSearchParams();
  const { data, isSuccess, isLoading, isError } = useQuery(
    ["event", event.slug, "tickets"],
    () => {
      let url = `/api/events/${event.slug}/tickets`;
      const ref = params.get("ref");
      const con = params.get("con") ?? event.contributor_id;
      if (con) {
        url += `?con=${con}`;
      }
      if (ref) {
        url += `?ref=${ref}`;
      }

      return axiosInstance.get(url).then((res) => res.data) as Promise<
        Array<TicketType>
      >;
    }
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="w-full h-6 rounded animate-pulse bg-theme-gray"></div>
        <div className="w-2/3 h-6 rounded animate-pulse bg-theme-gray"></div>
      </div>
    );
  }

  if (!isSuccess) {
    return <div>Gagal memuat data tiket</div>;
  }

  return (
    <div className="w-full">
      <TicketForm event={event} tickets={data} />
    </div>
  );
}

function TicketForm({
  event,
  tickets,
}: {
  event: EventType;
  tickets: Array<TicketType>;
}) {
  const router = useRouter();
  const pageSlug = event.current_slug ?? event.slug;
  const {
    handleSubmit,
    setValue,
    watch,
    setError,
    getValues,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      tickets: tickets.map((ticket) => ({
        id: ticket.id,
        quantity: 0,
        price: ticket.price,
        type: ticket.ticket_type,
      })),
    },
  });

  const params = useSearchParams();

  const { track } = usePixelTracking({
    eventSlug: event.slug,
  });

  const { mutateAsync, isLoading, isSuccess, error } = useMutation(
    (tickets: TicketOptionType[]) => {
      return axiosInstance
        .post(`/api/events/${event.slug}/orders`, {
          tickets: tickets.filter((t) => t.quantity > 0),
        })
        .then((res) => res.data) as Promise<{}>;
    },
    {
      onError: (e) => {
        if (e instanceof AxiosError) {
          if (e.response?.status === 422) {
            for (const [key, value] of Object.entries(e.response.data.errors)) {
              if (Array.isArray(value)) {
                setError(key as any, {
                  type: "server",
                  message: value[0] as string,
                });
              } else {
                setError(key as any, {
                  type: "server",
                  message: value as string,
                });
              }
            }
          }
        } else if (e instanceof Error) {
          console.error(e.message);
        }
      },
    }
  );

  const ticketsValue = watch("tickets");

  const loading = isLoading || isSuccess;
  const hasCheckoutableTickets = tickets.some((ticket) => ticket.can_checkout);

  const [ticketInfo, setTicketInfo] = useState(false);

  const handlerTicketInfo = () => {
    setTicketInfo(!ticketInfo)
  }

  if (tickets.length === 0) {
    return (
      <>
        <div className="text-center text-theme-gray">
          <Icons.ticket className="w-12 h-12 mx-auto mb-4" />
          <p>Penjualan tiket telah berakhir</p>
        </div>
      </>
    )
  }
 
  return (
    <form onSubmit={(e) => {
        e.preventDefault();
        router.prefetch(`/${pageSlug}/purchase`);
        const selectedTickets = getValues("tickets");

          // We use getValues to bypass react-hook-form validation
          mutateAsync(selectedTickets, {
            onSuccess: (data) => {
              track({
                eventName: "AddToCart",
                metadata: buildTicketSelectionMetadata({
                  event,
                  selectedTickets,
                }),
              });

              queryClient.invalidateQueries(["order"]);
              router.push(`/${pageSlug}/purchase?` + params.toString(), {
                scroll: false,
              });
            },
          });
        }}
        className="w-full"
      >
        <h3 className="text-lg font-semibold text-white dark:text-theme-dark">Pilih Tiket Anda</h3>
        <p className="text-theme-gray">
          Pilih tiket dan Jumlah yang ingin anda beli
        </p>

        <div className="text-white dark:text-theme-dark">
          {tickets.length > 0 ? [...tickets]
            .sort((a, b) => {
              if (a.can_checkout !== b.can_checkout) {
                return a.can_checkout ? -1 : 1;
              }

              return ((a as any).special === 1 ? 1 : 0) - ((b as any).special === 1 ? 1 : 0);
            })
            .map((ticket) => (
              (() => {
                const i = tickets.findIndex((t) => t.id === ticket.id);
                const isShowcase = ticket.visibility === "showcase";
                return (
                <div
                  className={cn(
                  "py-4 space-y-2 border-b border-gray-700 dark:border-theme-gray/20",
                  "relative",
                  (ticket as any).special === 1
                    ? "rounded-lg px-4 border-2 border-green-400 dark:border-green-300/30 bg-gradient-to-r from-green-900 to-transparent dark:from-green-600/50 shadow-sm my-3"
                    : ""
                  )}
                  key={ticket.id}
                >
                  {/* {(ticket as any).special === 1 && (
                  <div className="absolute top-3 right-3">
                    <Badge className="bg-green-400/10 text-green-300 dark:bg-green-600/20 dark:text-green-200 px-2 py-1 rounded-full text-xs font-semibold">
                    Promo
                    </Badge>
                  </div>
                  )} */}

                  <div className="flex items-center space-x-4">
                  <div className="w-full space-y-2 font-medium">
                    <div className={cn("text-gray-400 dark:text-theme-gray", (ticket as any).special === 1 ? "text-green-200 dark:text-black font-semibold" : "")}>
                    {ticket.name}
                    {isShowcase ? (
                      <Badge className="ml-2 bg-theme-accent/10 px-2 py-1 text-theme-accent dark:bg-theme-accent/20 dark:text-theme-accent">
                        Showcase
                      </Badge>
                    ) : null}
                    {ticket.session_entitlement_label ? (
                      <Badge className="ml-2 bg-blue-500/10 px-2 py-1 text-blue-300 dark:bg-blue-400/20 dark:text-blue-200">
                        {ticket.session_entitlement_label}
                      </Badge>
                    ) : ticket.session_scope === "single" && ticket.event_session ? (
                      <Badge className="ml-2 bg-violet-500/10 px-2 py-1 text-violet-300 dark:bg-violet-400/20 dark:text-violet-200">
                        {ticket.event_session.name}
                      </Badge>
                    ) : null}
                    </div>
                    {ticket.ticket_type === "minimum_price" ? (
                    <div className="space-y-1">
                      <NumberField
                      minValue={ticket.price}
                      defaultValue={ticket.price}
                      formatOptions={{
                        style: "currency",
                        currency: "IDR",
                        currencyDisplay: "narrowSymbol",
                        currencySign: "accounting",
                        maximumFractionDigits: 0,
                      }}
                      onChange={(value) => {
                        setValue(`tickets.${i}.price`, value);
                      }}
                      />
                      <div className="text-xs text-theme-gray">
                      <span>Minimum </span>
                      <span className="text-white">
                        {event.currency_type == 'JPY' ? formatYen(ticket.price) : event.currency_type == 'USD' ? formatDollar(ticket.price) : formatRupiah(ticket.price)}
                      </span>
                      </div>
                    </div>
                    ) : (
                    <>
                      <div className={cn("px-4 font-bold dark:text-theme-dark", (ticket as any).special === 1 ? "text-green-100" : "")}>
                      {event.currency_type == 'JPY' ? formatYen(ticket.price) : event.currency_type == 'USD' ? formatDollar(ticket.price) : formatRupiah(ticket.price)}
                      {ticket.ticket_type === "fixed_price" &&
                        ticket.regular_price !== ticket.price ? (
                        <span className="ml-2 text-sm text-gray-500 line-through">
                        {event.currency_type == 'JPY' ? formatYen(ticket.regular_price) : event.currency_type == 'USD' ? formatDollar(ticket.regular_price) : formatRupiah(ticket.regular_price)}
                        </span>
                      ) : null}
                      </div>
                    </>
                    )}
                  </div>

                  {!ticket.can_checkout ? (
                    <div className="shrink-0 text-right text-theme-accent"></div>
                  ) : ticket.tickets_left < 1 ? (
                    <Badge variant="destructive" className="shrink-0">
                    Sold Out
                    </Badge>
                  ) : (
                    <div className="flex flex-col items-center mt-3">
                    <QuantityField
                      label="Jumlah"
                      minValue={0}
                      defaultValue={0}
                      maxValue={Math.min(ticket.tickets_left, 30)}
                      onChange={(value) => {
                        setValue(`tickets.${i}.quantity`, value);
                      }}
                    />
                    {ticket.show_tickets_left ? (
                      <p className="mt-2 text-center text-xs text-theme-gray">
                        {ticket.tickets_left} tiket tersisa
                      </p>
                    ) : null}
                    </div>
                  )}
                  </div>

                  <p className={cn("text-sm italic whitespace-pre-line text-theme-gray", (ticket as any).special === 1 ? "text-green-100/90 dark:text-black" : "")}>
                  {ticket.description}
                  </p>

                  <div>
                  {errors.tickets?.[i]?.quantity ? (
                    <div className="mt-2 text-sm text-red-500">
                    {errors.tickets?.[i]?.quantity?.message}
                    </div>
                  ) : null}
                  {errors.tickets?.[i]?.price ? (
                    <div className="mt-2 text-sm text-red-500">
                    {errors.tickets?.[i]?.price?.message}
                    </div>
                  ) : null}
                  </div>
                </div>
                )
              })()
          )) : (
            <div className="py-4 text-center text-theme-gray">
              Penjualan tiket telah berakhir
            </div>
          )}
        </div>

        {errors.tickets?.message ? (
          <div className="mt-2 text-sm text-red-500">
            {errors.tickets?.message}
          </div>
        ) : null}

        {tickets.length > 0 ?
          <>
            {!hasCheckoutableTickets ? (
              <div className="pb-4 text-sm text-theme-accent py-4">
                Tiket belum tersedia untuk checkout online.
              </div>
            ) : null}
            <div className="py-4">
              <div className="flex items-center justify-between">
                <div className="text-white dark:text-theme-dark">Total</div>
                <div className="font-bold text-white dark:text-theme-dark">
                  {event.currency_type == 'JPY' ?
                    formatYen(
                      ticketsValue.reduce(
                        (acc, ticket, i) => acc + ticket.price * (ticket.quantity ?? 0),
                        0
                      )
                    )
                    : event.currency_type == 'USD' ?
                      formatDollar(
                        ticketsValue.reduce(
                          (acc, ticket, i) => acc + ticket.price * (ticket.quantity ?? 0),
                          0
                        )
                      )
                      : formatRupiah(
                        ticketsValue.reduce(
                          (acc, ticket, i) => acc + ticket.price * (ticket.quantity ?? 0),
                          0
                        )
                      )}
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 py-3 text-lg font-medium bg-theme-accent hover:bg-theme-accent/80"
              disabled={loading || !hasCheckoutableTickets}
            >
              {loading && <Icons.spinner className="w-4 h-4 mr-2 animate-spin" />}
              <span>Beli Tiket</span>
            </Button>
          </>
        : null }
    </form>
  );
}
